import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { Select } from '@/components/ui/select';
import { ApiError, api } from '@/lib/api';

/**
 * 有効期限の選択肢。既定は 90 日。
 *
 * 無期限は選べるが既定にはしない。一度配った鍵が誰の手も離れたまま
 * 永久に生き続けるのを、既定の振る舞いにしないため。
 * 値は select の都合で文字列。空文字が無期限を表す。
 */
const EXPIRY_OPTIONS = [
  { value: '30', label: '30 日' },
  { value: '90', label: '90 日' },
  { value: '180', label: '180 日' },
  { value: '365', label: '365 日' },
  { value: '', label: '無期限' },
] as const;

const DEFAULT_EXPIRY = '90';

/**
 * トークンの発行。
 *
 * 平文はこの応答でしか返らない。画面を離れると二度と出ないので、
 * その場で必ずコピーできるようにする。
 */
export function TokenIssueForm({
  issued,
  onIssued,
}: {
  /** 発行直後の平文。まだ発行していなければ null */
  issued: string | null;
  onIssued: (token: string) => void;
}) {
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState<string>(DEFAULT_EXPIRY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function issue() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ token: string }>('/api/tokens', {
        name: name.trim() || 'MCP クライアント',
        // 無期限だけは明示的に null を送る (キーを省くと既定の 90 日になる)
        expiresInDays: expiry === '' ? null : Number(expiry),
      });
      setName('');
      setExpiry(DEFAULT_EXPIRY);
      onIssued(result.token);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 border border-line bg-surface-raised px-4 py-5 md:px-6">
      <span className="text-sm font-bold">トークンを発行する</span>
      {/*
       * 狭い幅では折り返す。`input` は既定で 20 文字分の固有幅を持ち、
       * `flex-1` だけではそこまでしか縮まないので `min-w-0` も要る
       * (これが無いと名前・有効期限・発行の 3 つで 375px を超える)。
       */}
      <div className="flex flex-wrap gap-2 md:flex-nowrap">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="用途がわかる名前（例: 仕事用 Mac）"
          className="min-w-0 flex-1 border border-line-bold bg-surface-strong px-3 py-2 text-sm focus:outline-2 focus:outline-offset-0 focus:outline-brand-700"
        />
        <Select
          value={expiry}
          onChange={(e) => setExpiry(e.target.value)}
          aria-label="有効期限"
          className="w-32"
        >
          {EXPIRY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <Button variant="primary" onClick={issue} disabled={busy}>
          発行
        </Button>
      </div>
      {error ? <p className="text-sm text-alert">{error}</p> : null}

      {issued ? (
        <div className="flex flex-col gap-2 border border-brand-300 bg-brand-50 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[13px] font-medium text-brand-700">
              この画面を離れると二度と表示されません。いますぐコピーしてください。
            </span>
            <CopyButton value={issued} />
          </div>
          <code className="font-mono text-xs break-all text-ink">{issued}</code>
        </div>
      ) : null}
    </div>
  );
}
